import { type OrganizationInfo, type UserProfile } from "~/features/auth/types/keycloak";

const SELECTED_TENANT_STORAGE_KEY = "selected_tenant_id";

interface ResolvedTenantSelection {
  user: UserProfile;
  selectedTenantId: string | null;
  availableOrganizations: OrganizationInfo[];
}

const applyOrganizationSelection = (
  user: UserProfile,
  organization?: OrganizationInfo,
): UserProfile => {
  if (!organization) {
    return user;
  }

  return {
    ...user,
    tenantId: organization.tenantId,
    organization: organization.name,
    country: organization.country,
  };
};

const getStoredSelectedTenantId = (): string | null =>
  localStorage.getItem(SELECTED_TENANT_STORAGE_KEY);

export const storeSelectedTenantId = (tenantId: string): void => {
  localStorage.setItem(SELECTED_TENANT_STORAGE_KEY, tenantId);
};

export const clearStoredSelectedTenantId = (): void => {
  localStorage.removeItem(SELECTED_TENANT_STORAGE_KEY);
};

export const resolveTenantSelection = (user: UserProfile): ResolvedTenantSelection => {
  const storedTenantId = getStoredSelectedTenantId();
  const selectedOrganization = storedTenantId
    ? user.organizations.find((organization) => organization.tenantId === storedTenantId)
    : undefined;

  const resolvedUser = applyOrganizationSelection(user, selectedOrganization);

  return {
    user: resolvedUser,
    selectedTenantId: selectedOrganization?.tenantId ?? user.tenantId,
    availableOrganizations: user.organizations,
  };
};

export const switchUserTenant = (
  user: UserProfile,
  availableOrganizations: OrganizationInfo[],
  tenantId: string,
): UserProfile | null => {
  const selectedOrganization = availableOrganizations.find(
    (organization) => organization.tenantId === tenantId,
  );

  if (!selectedOrganization) {
    return null;
  }

  return applyOrganizationSelection(user, selectedOrganization);
};
